import React, { useEffect, useState } from 'react';
import { getToken } from '../../utils/getToken';
import { NumberTo2DP } from '../../utils/NumberTo2DP';

const baseURL = process.env.REACT_APP_BASE_URL || 'https://api.quickcare.ng/admin';

export default function OverviewTable({ period, country }) {
  const [response, setResponse] = useState({loading: true, error:false, data: []});

    useEffect(() => {
        const fetchData = async () => {
            const token = getToken();
            if (!token) {
                setResponse({loading: false, error:true, data:[]})
                return;
            }

            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const requestOptions = {
                method: "GET",
                headers: myHeaders,
                redirect: "follow"
            };

            setResponse({loading: true, error:false, data: []})

            try {
                const response = await fetch(`${baseURL}/overview/table-data?start_date=${period.start_date}&end_date=${period.end_date}&country=${country}`, requestOptions);
                const result = await response.json();
                console.log("Data received...:", result);
                setResponse({loading: false, error:false, data: result.data})
            } catch (error) {
                setResponse({loading: false, error:true, data:[]})
            }
        };

        fetchData();
    }, [period, country]);

    let totalPlayers = response?.data.length ? response?.data?.map(item => item?.total_players ?  item?.total_players : 0)?.reduce((accumulator, item) => {
        return accumulator += item;
    }, 0) : 0

    let totalRevenue = response?.data.length ? response?.data?.map(item => item?.total_revenue ? item?.total_revenue : 0)?.reduce((accumulator, item) => {
        return accumulator += item;
    }, 0) : 0

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    // Get country flag emoji
    const getCountryFlag = (country) => {
        const flags = {
            'kenya': '🇰🇪',
            'nigeria': '🇳🇬',
            'ghana': '🇬🇭',
            'south africa': '🇿🇦',
            'tanzania': '🇹🇿',
            'uganda': '🇺🇬'
        };
        return flags[country?.toLowerCase()] || '🌍';
    };

    return (
        <div className="w-full">
            {/* Table Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
                <div className="flex items-center gap-2">
                    <div className="w-1 h-6 bg-gradient-to-b from-[#069125] to-[#057a1f] rounded-full"></div>
                    <h1 className="text-lg font-bold text-gray-800">Overview Table</h1>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    <span>{response.loading ? 'Loading...' : `${response.data.length} records`}</span>
                </div>
            </div>

            {/* Table Container */}
            <div className="overflow-hidden rounded-2xl border border-gray-100 shadow-sm bg-white">
                {/* Desktop Table View */}
                <div className="hidden lg:block overflow-x-auto">
                    <table className='w-full'>
                        <thead>
                            <tr className='bg-gradient-to-r from-gray-50 to-gray-100/80 border-b border-gray-200'>
                                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    <span className="flex items-center gap-1">🌍 Country</span>
                                </th>
                                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    <span className="flex items-center gap-1">🔑 Keyword</span>
                                </th>
                                <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    <span className="flex items-center justify-end gap-1">👥 Total Players</span>
                                </th>
                                <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    <span className="flex items-center justify-end gap-1">💰 Revenue</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {response.loading ? (
                                <tr>
                                    <td colSpan={4} className="px-4 py-12 text-center">
                                        <div className="flex flex-col items-center gap-3">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#069125]"></div>
                                            <span className="text-sm text-gray-500">Loading data...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : response.error ? (
                                <tr>
                                    <td colSpan={4} className="px-4 py-12 text-center">
                                        <div className="flex flex-col items-center gap-2">
                                            <span className="text-3xl">⚠️</span>
                                            <span className="text-sm text-gray-500">Oops! Error occurred while fetching data</span>
                                            <button 
                                                onClick={() => window.location.reload()} 
                                                className="mt-2 px-4 py-2 bg-[#069125] text-white text-sm rounded-lg hover:bg-[#057a1f] transition-colors"
                                            >
                                                Retry
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : (response && response?.data?.length) ? (
                                <>
                                    {response.data.map((item, index) => (
                                        <tr 
                                            key={`${index}`} 
                                            className={`border-b border-gray-50 hover:bg-green-50/30 transition-colors duration-150 ${
                                                index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                                            }`}
                                        >
                                            <td className="px-4 py-3 text-sm text-gray-800">
                                                <span className="flex items-center gap-2">
                                                    <span className="text-lg">{getCountryFlag(item.country)}</span>
                                                    <span className="capitalize">{item.country}</span>
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-800">
                                                <span className="inline-block px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                                                    {item.keyword}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-800 text-right font-semibold">
                                                {item.total_players.toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-800 text-right font-semibold text-green-600">
                                                {formatCurrency(item.total_revenue)}
                                            </td>
                                        </tr>
                                    ))}
                                    {/* Total Row */}
                                    <tr className='bg-gradient-to-r from-[#069125]/5 to-[#057a1f]/5 border-t-2 border-[#069125]/20'>
                                        <td className="px-4 py-3.5 text-sm font-bold text-gray-800" colSpan="2">
                                            <span className="flex items-center gap-2">
                                                <span>📊</span> TOTAL
                                            </span>
                                        </td>
                                        <td className="px-4 py-3.5 text-sm font-bold text-gray-800 text-right">
                                            {totalPlayers.toLocaleString()}
                                        </td>
                                        <td className="px-4 py-3.5 text-sm font-bold text-green-700 text-right">
                                            {formatCurrency(totalRevenue)}
                                        </td>
                                    </tr>
                                </>
                            ) : (
                                <tr>
                                    <td colSpan={4} className="px-4 py-12 text-center">
                                        <div className="flex flex-col items-center gap-2">
                                            <span className="text-3xl">📭</span>
                                            <span className="text-sm text-gray-500">No records found</span>
                                            <span className="text-xs text-gray-400">Try adjusting your filters</span>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden">
                    {response.loading ? (
                        <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#069125] mx-auto"></div>
                            <span className="text-sm text-gray-500 mt-2 block">Loading data...</span>
                        </div>
                    ) : response.error ? (
                        <div className="p-8 text-center">
                            <span className="text-3xl block">⚠️</span>
                            <span className="text-sm text-gray-500 block mt-2">Error occurred</span>
                        </div>
                    ) : (response && response?.data?.length) ? (
                        <div className="divide-y divide-gray-100">
                            {response.data.map((item, index) => (
                                <div key={index} className="p-4 hover:bg-green-50/30 transition-colors">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <span className="text-2xl">{getCountryFlag(item.country)}</span>
                                            <div>
                                                <h3 className="font-semibold text-gray-800 capitalize">{item.country}</h3>
                                                <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium mt-1">
                                                    {item.keyword}
                                                </span>
                                            </div>
                                        </div>
                                        <span className="text-xs text-gray-400">#{index + 1}</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 mt-3">
                                        <div className="bg-gray-50 rounded-xl p-3">
                                            <p className="text-xs text-gray-500">Total Players</p>
                                            <p className="text-lg font-bold text-gray-800">{item.total_players.toLocaleString()}</p>
                                        </div>
                                        <div className="bg-green-50 rounded-xl p-3">
                                            <p className="text-xs text-gray-500">Revenue</p>
                                            <p className="text-lg font-bold text-green-600">{formatCurrency(item.total_revenue)}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {/* Total Card */}
                            <div className="p-4 bg-gradient-to-r from-[#069125]/10 to-[#057a1f]/10">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm font-bold text-gray-800">📊 TOTAL</span>
                                    <span className="text-xs text-gray-400">Summary</span>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-white/70 rounded-xl p-3">
                                        <p className="text-xs text-gray-500">Total Players</p>
                                        <p className="text-lg font-bold text-gray-800">{totalPlayers.toLocaleString()}</p>
                                    </div>
                                    <div className="bg-white/70 rounded-xl p-3">
                                        <p className="text-xs text-gray-500">Total Revenue</p>
                                        <p className="text-lg font-bold text-green-600">{formatCurrency(totalRevenue)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="p-8 text-center">
                            <span className="text-3xl block">📭</span>
                            <span className="text-sm text-gray-500 block mt-2">No records found</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}